import { COLOR_PALETTES } from "@/components/create/constants";

const HEX_COLOR_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const COLOR_KEYWORDS: Array<{ pattern: RegExp; hex: string }> = [
  { pattern: /\bdusty\s+blue\b/, hex: "#7DA2B8" },
  { pattern: /\bpowder\s+blue\b/, hex: "#B0E0E6" },
  { pattern: /\bice\s+blue\b/, hex: "#D6EDF5" },
  { pattern: /\bbaby\s+blue\b/, hex: "#89CFF0" },
  { pattern: /\bsky\s+blue\b/, hex: "#87CEEB" },
  { pattern: /\bsteel\s+blue\b/, hex: "#4682B4" },
  { pattern: /\bslate\s+blue\b/, hex: "#6A7F99" },
  { pattern: /\bperiwinkle\b/, hex: "#CCCCFF" },
  { pattern: /\bcornflower\b/, hex: "#6495ED" },
  { pattern: /\bcaribbean\s+blue\b/, hex: "#00A6D6" },
  { pattern: /\bmidnight\s+navy\b/, hex: "#111936" },
  { pattern: /\bmidnight\s+ink\b|\bink\b/, hex: "#111936" },
  { pattern: /\bsapphire\b/, hex: "#0F52BA" },
  { pattern: /\bdusty\s+sage\b/, hex: "#8EA985" },
  { pattern: /\beucalyptus\b/, hex: "#8AA998" },
  { pattern: /\bmuted\s+olive\b/, hex: "#6F7D4B" },
  { pattern: /\bolive\b/, hex: "#708238" },
  { pattern: /\bsage\b/, hex: "#9DC183" },
  { pattern: /\bseafoam\b/, hex: "#9FE2BF" },
  { pattern: /\bmint\b/, hex: "#98FF98" },
  { pattern: /\bivory\b|\balabaster\b|\bporcelain\b/, hex: "#FFFFF0" },
  { pattern: /\bchampagne\b/, hex: "#F7E7CE" },
  { pattern: /\bblush\b/, hex: "#FFE4E1" },
  { pattern: /\bpearl\b/, hex: "#F8F6F0" },
  { pattern: /\bnavy\b/, hex: "#000080" },
  { pattern: /\broyal\s+blue\b/, hex: "#4169E1" },
  { pattern: /\bblue\b/, hex: "#4169E1" },
  { pattern: /\bburgundy\b|\bwine\b|\bmerlot\b/, hex: "#800020" },
  { pattern: /\bemerald\b/, hex: "#50C878" },
  { pattern: /\bcharcoal\b/, hex: "#36454F" },
  { pattern: /\bdusty\s+rose\b/, hex: "#DCAE96" },
  { pattern: /\blavender\b/, hex: "#E6E6FA" },
  { pattern: /\bterracotta\b/, hex: "#E2725B" },
  { pattern: /\bmauve\b/, hex: "#E0B0FF" },
  { pattern: /\bcoral\b/, hex: "#FF7F50" },
  { pattern: /\bpeach\b/, hex: "#FFDAB9" },
  { pattern: /\blilac\b/, hex: "#C8A2C8" },
  { pattern: /\baqua\b|\bturquoise\b/, hex: "#00BFC4" },
  { pattern: /\bhot\s+pink\b/, hex: "#FF69B4" },
  { pattern: /\bpink\b/, hex: "#FFC0CB" },
  { pattern: /\bgold\b/, hex: "#FFD700" },
  { pattern: /\bsilver\b|\bgrey\b|\bgray\b/, hex: "#C0C0C0" },
  { pattern: /\bpurple\b|\bviolet\b/, hex: "#800080" },
  { pattern: /\bgreen\b/, hex: "#008000" },
  { pattern: /\bred\b/, hex: "#FF0000" },
  { pattern: /\bblack\b/, hex: "#000000" },
  { pattern: /\bwhite\b/, hex: "#FFFFFF" },
];

export const FALLBACK_SWATCH_COLORS = ["#F8F6F0", "#F4D7DE", "#A8B8FF"];

const PALETTE_SWATCHES = Object.values(COLOR_PALETTES)
  .flat()
  .sort((a, b) => b.name.length - a.name.length);

function normalizeText(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const match = value.trim().match(HEX_COLOR_RE);
  if (!match) return null;

  const raw = match[1];
  const expanded =
    raw.length === 3
      ? raw
          .split("")
          .map(char => `${char}${char}`)
          .join("")
      : raw;

  return `#${expanded.toUpperCase()}`;
}

export function normalizeColorHexes(value: unknown): string[] {
  const values = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/\s*,\s*/)
      : [value];
  return values
    .map(normalizeHexColor)
    .filter((hex): hex is string => Boolean(hex));
}

export function resolveColorHex(colorName: string): string | null {
  const directHex = normalizeHexColor(colorName);
  if (directHex) return directHex;

  const normalized = normalizeText(colorName);
  if (!normalized) return null;

  const exactPaletteMatch = PALETTE_SWATCHES.find(
    color => normalized === normalizeText(color.name)
  );
  if (exactPaletteMatch) return normalizeHexColor(exactPaletteMatch.hex);

  // A descriptive shade is not interchangeable with any familiar color word
  // it contains: "light icy blue" must never become saturated royal blue.
  // Known aliases remain available; novel shades need an explicit swatch.
  const keywordMatch = COLOR_KEYWORDS.find(({ pattern }) => {
    const match = normalized.match(pattern);
    return match?.index === 0 && match[0].length === normalized.length;
  })?.hex;
  if (keywordMatch) return keywordMatch;
  return null;
}

function colorParts(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((part): part is string => typeof part === "string");
  }
  if (typeof value !== "string") return [];
  return value.split(/\s+(?:or|and|with)\s+|[,/+]/i);
}

export function resolveColorHexes(value: unknown): string[] {
  const hexes = colorParts(value).map(part => resolveColorHex(part.trim()));
  // A swatch for only the recognized half would misrepresent the whole
  // selected color and could then be persisted by enrichColorData.
  return hexes.every((hex): hex is string => hex !== null) ? hexes : [];
}

export function getColorHexesForValue(
  colorValue: unknown,
  explicitHexValue?: unknown
): string[] {
  const explicitHexes = normalizeColorHexes(explicitHexValue);
  const authoredParts = Array.isArray(explicitHexValue)
    ? explicitHexValue
    : typeof explicitHexValue === "string"
      ? explicitHexValue.split(/\s*,\s*/)
      : [];
  const partCount = colorParts(colorValue).length;
  if (
    explicitHexes.length > 0 &&
    explicitHexes.length === authoredParts.length &&
    (partCount === 0 || explicitHexes.length === partCount)
  )
    return explicitHexes;
  return resolveColorHexes(colorValue);
}

export function getColorHexesForSwatch(
  colorValue: unknown,
  explicitHexValue?: unknown
): string[] {
  return getColorHexesForValue(colorValue, explicitHexValue);
}

export function colorHexForColorValue(
  colorValue: unknown
): string | string[] | undefined {
  const hexes = resolveColorHexes(colorValue);
  if (hexes.length === 0) return undefined;
  return hexes.length === 1 ? hexes[0] : hexes;
}

export function enrichColorData<T extends Record<string, unknown>>(data: T): T {
  if (data.colorHex || !data.color) return data;
  const colorHex = colorHexForColorValue(data.color);
  if (!colorHex) return data;
  return { ...data, colorHex };
}

export function formatColorWithHex(
  colorValue: string | string[],
  colorHexValue?: string | string[]
): string {
  const colorParts = Array.isArray(colorValue)
    ? colorValue.filter(Boolean)
    : colorValue
        .split(/\s+(?:and|with)\s+|[,/+]/i)
        .map(part => part.trim())
        .filter(Boolean);
  const colorText = Array.isArray(colorValue)
    ? colorParts.join(", ")
    : colorValue.trim();
  const hexes = normalizeColorHexes(colorHexValue);

  if (!colorText || hexes.length === 0) return colorText;

  if (colorParts.length > 1 && hexes.length === colorParts.length) {
    return colorParts
      .map((part, index) => `${part} (${hexes[index]})`)
      .join(", ");
  }

  return `${colorText} (${hexes.join(", ")})`;
}
